import { Injectable } from '@angular/core';
import { MomentService } from '@fc-core/services/moment.service';
import type { Vehicle } from '@fc-core/models/vehicle/vehicle.model';
import type {
  VehicleType,
  VehicleTypeDetail,
} from '@fc-core/models/vehicle/vehicle-type.model';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { map } from 'rxjs/operators';
import { BackendResponse } from '@fc-core/models/response.model';
import { DATE_TIME_FORMATS } from '@fc-shared/utils/date-time-formats';
import { VehicleStatsChartData } from '@fc-vehicles/models/vehicle-stats-chart-data';

@Injectable({
  providedIn: 'root',
})
export class VehicleService {
  constructor(
    private momentService: MomentService,
    private httpClient: HttpClient,
  ) {}

  static hasWarnings(vehicle: Vehicle): boolean {
    if (!vehicle.lastReading) {
      return false;
    }
    return (
      Math.floor(
        (new Date().getTime() -
          new Date(vehicle.lastReading.datetime).getTime()) /
          1000 /
          3600,
      ) > 1 && vehicle.status === 'active'
    );
  }

  getVehicleTypes(onlyUsed = false): Observable<VehicleType[]> {
    return this.httpClient
      .get<
        BackendResponse<VehicleType[]>
      >(`${environment.apiUrl}api/vehicle-types/${onlyUsed ? '?used=True' : ''}`)
      .pipe(
        map((response: BackendResponse<VehicleType[]>) =>
          Array.isArray(response.results) ? response.results : [],
        ),
      );
  }

  getVehicleTypeDetail(typeId: number): Observable<VehicleTypeDetail> {
    return this.httpClient.get<VehicleTypeDetail>(
      `${environment.apiUrl}api/vehicle-types/${typeId}/`,
    );
  }

  levelsDataProcessing(
    levelData: any,
    param: string,
  ): VehicleStatsChartData | undefined {
    const levelObject: VehicleStatsChartData = {
      data: [],
      current: {
        value: '',
        date: '',
      },
    };
    if (levelData.results) {
      if (
        levelData.results.filter((item: any) => item[param] === 0).length ===
        levelData.results.length
      ) {
        levelObject.data = [];
        return levelObject;
      }

      levelObject.data = levelData.results.map((item: any) => {
        const hasRange = Number.isFinite(item.min) && Number.isFinite(item.max);
        return {
          x: item.date,
          y: item[param],
          ...(hasRange ? { min: item.min, max: item.max } : {}),
        };
      });

      const latestValue = levelData.latest?.value ?? levelData.latest?.[param];
      if (typeof latestValue === 'number') {
        levelObject.current.value = latestValue.toFixed(1);
      }

      levelObject.current.date = this.momentService
        .moment(levelData.latest.date)
        .format(DATE_TIME_FORMATS.displayTimestamp);
      return levelObject;
    }
  }
}
